<script setup>
import ArrowSquareOutIcon from '@iconify-vue/ph/arrow-square-out';
import InfoIcon from '@iconify-vue/ph/info';
import { Link } from '@inertiajs/vue3';
import { formatNumber } from '@js/utils/formatter';
import { AnimatePresence, Motion } from 'motion-v';
import {
    TooltipContent,
    TooltipPortal,
    TooltipProvider,
    TooltipRoot,
    TooltipTrigger,
} from 'reka-ui';
import { computed, ref } from 'vue';

const {
    number,
    align = 'left',
    suffix,
    tooltipContent,
    color,
    type,
    withDecimals = false,
    withPlus = false,
    variation = undefined,
    variationWithDecimals = false,
    variationWithPlus = false,
    href = null,
    inertia = false,
    target = '_blank',
} = defineProps({
    number: {
        type: [Number, String],
        required: true,
    },
    align: {
        type: String,
        required: false,
    },
    href: {
        type: String,
        required: false,
    },
    inertia: {
        type: Boolean,
        required: false,
    },
    target: {
        type: String,
        required: false,
    },
    suffix: {
        type: String,
        required: false,
    },
    tooltipContent: {
        type: String,
        required: false,
    },
    color: {
        type: String,
        required: false,
    },
    type: {
        type: String,
        required: false,
    },
    withDecimals: {
        type: Boolean,
        required: false,
    },
    withPlus: {
        type: Boolean,
        required: false,
    },
    variation: { type: [Number, String], required: false },
    variationWithDecimals: { type: Boolean, required: false },
    variationWithPlus: { type: Boolean, required: false },
});

const isTooltipOpen = ref(false);

const hasTooltipContent = computed(
    () =>
        tooltipContent !== null &&
        tooltipContent !== '' &&
        typeof tooltipContent !== 'undefined',
);
</script>

<template>
    <div
        class="flex h-full items-center p-4 text-lg font-bold whitespace-nowrap"
        :class="{
            'justify-start': align === 'left',
            'justify-center text-center': align === 'center',
            'justify-end': align === 'right',
            'text-green-500': color === 'green' || type === 'success',
            'text-red-500': color === 'red' || type === 'danger',
            'text-orange-500': color === 'orange' || type === 'warning',
        }"
    >
        <component
            :is="href ? (inertia ? Link : 'a') : 'span'"
            :href="href || undefined"
            :target="href && !inertia ? target : undefined"
            :class="{ 'inline-flex items-center hover:underline': href }"
        >
            {{ formatNumber(number, withDecimals, withPlus) }}

            <sup v-if="href" class="ml-1">
                <ArrowSquareOutIcon class="inline size-3" />
            </sup>
        </component>

        <template v-if="suffix">{{ suffix }}</template>

        <span
            v-if="variation"
            class="ml-2"
            :class="{
                'text-green-500': variation > 0,
                'text-red-500': variation < 0,
            }"
        >
            {{
                formatNumber(
                    variation,
                    variationWithDecimals,
                    variationWithPlus,
                )
            }}
        </span>

        <div
            v-if="hasTooltipContent"
            class="relative flex items-center justify-center pl-1"
        >
            <TooltipProvider>
                <TooltipRoot v-model:open="isTooltipOpen" :delay-duration="0">
                    <TooltipTrigger as-child>
                        <button type="button">
                            <InfoIcon
                                class="size-5 cursor-help text-indigo-600"
                            />
                        </button>
                    </TooltipTrigger>

                    <TooltipPortal>
                        <AnimatePresence>
                            <TooltipContent
                                v-if="isTooltipOpen"
                                as-child
                                force-mount
                                side="right"
                                :side-offset="5"
                            >
                                <Motion
                                    :initial="{ opacity: 0, scale: 0.96 }"
                                    :animate="{ opacity: 1, scale: 1 }"
                                    :exit="{ opacity: 0, scale: 0.96 }"
                                    :transition="{ duration: 0.15 }"
                                    class="wysiwyg z-50 max-h-[8rem] w-64 overflow-y-auto rounded bg-white px-2 py-1 drop-shadow-sm"
                                    v-html="tooltipContent"
                                />
                            </TooltipContent>
                        </AnimatePresence>
                    </TooltipPortal>
                </TooltipRoot>
            </TooltipProvider>
        </div>

        <div v-if="$slots.actions" class="ml-2 flex gap-2">
            <slot name="actions" />
        </div>
    </div>
</template>
